import React, { useEffect, useState } from "react";
import { Header } from "components/Header";
import { SearchBar } from "components/SearchBar";

import ActivationModal from "components/Modals/ActivationModal";
import { MorePolicemanDetailsModal } from "components/Modals/MorePolicemanDetailsModal";
import {
  getPolicemanService,
} from "services/PolicemenServices";
import {
  IPoliceman,
  IGetByIdPoliceman,
  IUpdatePolicemen,
} from "interfaces/Policeman";
import {
  handlePolicemanActiveSubmit,
  handlePolicemanDeleteSubmit,
} from "services/handles/PolicemanHandles";
import { orderBy } from "services/OrderBy";

import { DashboardTemplate } from "templates/DashboardTemplate";
import Button from "components/Button";

import { StepRegisterPoliceman } from "components/Modals/StepRegister/StepRegisterPoliceman";
import { usePolicemanRegister } from "contexts/register/PolicemanContext";
import { CheckAlert } from "utils/checkAlert";
import { payPolicemanService } from "services/PaymentServices";
import { useDataList } from "hooks/useDataList";
import { socket } from "hooks/useSocket";
import { IPolicemanRegister } from "contexts/register/IPolicemanContext";
import { useAuth } from "contexts/authContext";
import { IUser } from "interfaces/User";
import { accountData, IBankingDataProps } from "interfaces/BankingData";
import { IAddressProps } from "interfaces/Address";
import { IVehicleProps } from "interfaces/Vehicle";
import { ExportEfficiencyReportModal } from "components/Modals/ExportEfficiencyReportModal";
import { MoreModifiedPolicemanDetailsModal } from "components/Modals/MoreModifiedPolicemanDetailsModal";
import { treatSocketLocations } from "utils/treatSocketLocations";
import { FilterTabBar } from "components/FilterTabBar";
import { StepRegisterManyPoliceman } from "components/Modals/StepRegister/StepRegisterManyPoliceman";
import {
  DashboardEllipsedPolicemen,
  SearchBarContainer,
} from "./styles";
import { DataTable } from "components/DataTable";
import { allPolicemanTableColumns, IPolicemanWhereFilters, toApprovePolicemanTableColumns, updatePolicemanTableColumns } from "./data";
import debouncer from "utils/debouncer";
import { DataTableMenuItem } from "components/DataTable/DataTableMenu";

type FilterOptions = "all" | "working" | "toApprove" | "modified";
type Coords = {
  latitude: number;
  longitude: number;
};

export type MarkerType =
  | "policeman"
  | "occVehicle"
  | "occPatrimony"
  | "store"
  | "policemanOnDuty";

type Markers = {
  id: string;
  coords: Coords;
  type: MarkerType;
  name?: string;
  working?: boolean;
};

type SocketMarkers = {
  [id: string]: SocketMarkerBody;
};

export type SocketMarkerBody = {
  coords: Coords;
  token: string;
  working: boolean;
  name?: string;
};
export function Policemen() {
  const [moreInfoPoliceman, setMoreInfoPoliceman] =
    useState<IGetByIdPoliceman>();

  const {
    hasChanges,
    setHasChanges,
    setErrors,
    setPoliceman,
    setBankingData,
    setAddress,
    setVehicle,
    resetPoliceman,
  } = usePolicemanRegister();

  const { tenant, role } = useAuth();

  const [filter, setFilter] = useState<FilterOptions>("all");
  const [singlePoliceman, setSinglePoliceman] = useState<IPoliceman>();
  const [socketPolicemen, setSocketPolicemen] = useState<Markers[]>([]);
  const [page, setPage] = useState(1);
  const [isModifiedMoreDetailsOpen, setIsModifiedMoreDetailsOpen] =
    useState<boolean>(false);
  const [modifiedPolicemanDetails, setModifiedPolicemanDetails] =
    useState<IUpdatePolicemen>({} as IUpdatePolicemen);
  const [filterWhere, setFilterWhere] = useState<IPolicemanWhereFilters | undefined>()

  const [openEfficiencyModal, setOpenEfficiencyModal] =
    useState<boolean>(false);

  const isApproveTabSelected = () => {
    switch (filter) {
      case "all":
        return;
      case "toApprove":
        return { approved: false };
      case "working":
        return { approved: true };
      default:
        return;
    }
  };

  const policemen = useDataList<IPoliceman>({
    queryKey: 'listAllPolicemen',
    route: 'policemen',
    dependency: [page],
    filter: {
      size: 10,
      page,
      params: filterWhere,
      relations: { user: true },
      order: {
        user: {
          name:"ASC"
        }
      }
    }
  });

  const modifiedPolicemen = useDataList<IUpdatePolicemen>({
    queryKey: 'listAllPolicemenUpdate',
    route: 'policemen/updates',
    filter: {
      size: 10,
      page,
      params: filterWhere,
      relations: { user: true }
    }
  })

  function onLocationChange(locations: SocketMarkers) {
    const _locations = treatSocketLocations({
      socketLocations: locations,
      tenantId: tenant?.id ?? "",
    });

    setSocketPolicemen([..._locations]);
  }

  useEffect(() => {
    if (tenant?.id) {
      socket.on("onLocationChange", onLocationChange);
      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", onLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
  }, []);

  const [registerModal, setRegisterModal] = useState(false);

  const [activationModal, setActivationModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [moreDetails, setMoreDetails] = useState(false);
  const [
    isRegisterManyPolicemanModalOpen,
    setIsRegisterManyPolicemanModalOpen,
  ] = useState<boolean>(false);

  useEffect(() => {
    CheckAlert();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (singlePoliceman) {
      getPolicemanService(singlePoliceman.user.id).then((res) => {
        setMoreInfoPoliceman(res.item);
      });
    }
  }, [singlePoliceman]);

  function verifyPoliceman(id: string) {
    let isIncluded: "off" | "working" | "ready" = "off";
    for (let policeman of socketPolicemen) {
      if (policeman && policeman.id === id) {
        isIncluded = "ready";
        if (policeman.working) {
          isIncluded = "working";
        }
        break;
      }
    }
    return isIncluded;
  }

  function handleSearchChange(queryString: string) {
    if (queryString) {
      return setFilterWhere([
        { user: { name: `%${queryString}%` }, ...isApproveTabSelected() },
        { region: `%${queryString}%`, ...isApproveTabSelected() },
        { user: { cpf: `%${queryString}%` }, ...isApproveTabSelected() },
        { user: { email: `%${queryString}%` }, ...isApproveTabSelected() },
        { user: { rg: `%${queryString}%` }, ...isApproveTabSelected() },
      ])
    }
  }

  const debouncePolicemanSearch = debouncer(handleSearchChange, 800);

  function handleAllTableMenuClick(menuItemId: string, data: IPoliceman) {
    if (!data) return;

    setSinglePoliceman(data);

    switch (menuItemId) {
      case "details":
        setMoreDetails(!moreDetails);
        break;
      case "edit":
        setRegisterModal(!registerModal);
        break;
      case "activate":
        setActivationModal(!activationModal);
        break;
      case "deactivate":
        setDeleteModal(!deleteModal);
        break;
      case "pay":
        setPaymentModal(true);
        break;
      case "approve":
        setMoreDetails(true);
        setSinglePoliceman(data);
        break;
    }
  }

  return (
    <DashboardTemplate
      hasTableWrapper
      total={policemen.data?.total || 0}
      page={page}
      setPage={setPage}
    >
      <Header title="Agentes" />

      <FilterTabBar
        filterOptions={[
          {
            label: `Todos (${policemen?.data?.count?.all ?? 0})`,
            value: "all",
            onClick: () => {
              setFilter("all");
              setFilterWhere(undefined);
              setPage(1);
            },
          },
          {
            label: `Para aprovação (${policemen?.data?.count?.toApprove ?? 0})`,
            value: "toApprove",
            onClick: () => {
              setFilter("toApprove");
              setFilterWhere([{ approved: false }])
              setPage(1);
            },
          },
          {
            label: `Modificação (${modifiedPolicemen?.data?.items?.length ?? 0
              })`,
            value: "modified",
            onClick: () => {
              setFilter("modified");
            },
          },
        ]}
        activeOptionId={filter}
      />

      <SearchBarContainer>
        <SearchBar
          type="text"
          name="filter"
          id="filter"
          placeholder="Procure por nome, e-mail, CPF..."
          buttonText="Cadastrar agente"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.value) {
              debouncePolicemanSearch(event.target.value)
            } else {
              setFilterWhere(undefined);
            }
          }}
          required={false}
          onClick={() => setRegisterModal(true)}
          secondButton={true}
          secondButtonText="Relatório"
          secondOnClick={() => setOpenEfficiencyModal(true)}
        />
        <Button
          title="Cadastro em lote"
          onClick={() => setIsRegisterManyPolicemanModalOpen(true)}
        />
      </SearchBarContainer>

      <StepRegisterManyPoliceman
        opened={isRegisterManyPolicemanModalOpen}
        setOpened={setIsRegisterManyPolicemanModalOpen}
      />

      {filter === 'modified' ? (
        <DataTable
          isFetching={modifiedPolicemen.isFetching}
          data={modifiedPolicemen.data?.items}
          headers={updatePolicemanTableColumns}
          menuActions={[
            {
              id: "approve",
              label: "Analisar dados do agente",
              onClick: (_, data) => {
                setIsModifiedMoreDetailsOpen(true);
                setModifiedPolicemanDetails(data);
              },
            },
          ]}
        />
      ) : filter === 'toApprove' ? (
        <DataTable
          isFetching={policemen.isFetching}
          data={policemen.data?.items}
          headers={toApprovePolicemanTableColumns}
          menuActions={[
            {
              id: "approve",
              label: "Analisar dados do agente",
              onClick: handleAllTableMenuClick,
            },
          ]}
        />
      ) : (
        <DataTable
          isFetching={policemen.isFetching}
          data={policemen.data?.items}
          headers={[
            {
              field: 'user',
              label: 'Nome do pronta-resposta',
              renderColumn: (data) => {
                if (!data.approved) {
                  return data.user.name;
                }

                const verified = verifyPoliceman(data.id);

                return (
                  <DashboardEllipsedPolicemen variant={verified}>
                    {data.user.name}
                  </DashboardEllipsedPolicemen>
                )
              }
            },
            ...allPolicemanTableColumns
          ]}
          menuActions={[
            { id: "details", label: "Dados do agente", onClick: handleAllTableMenuClick, hide: (data) => data.approved },
            { id: "edit", label: "Editar dados do agente", onClick: handleAllTableMenuClick, hide: (data) => data.approved },
            {
              id: "activate",
              label: "Ativar agente",
              onClick: handleAllTableMenuClick,
              hide: (data) => role === "master" &&  (!data.active && data.approved),
            },
            {
              id: "deactivate",
              label: "Desativar agente",
              onClick: handleAllTableMenuClick,
              hide: (data) => role === "master" && (data.active && data.approved),
            },
            {
              id: "pay",
              label: "Declarar pagamento",
              onClick: handleAllTableMenuClick,
              hide: (data) => data.salary !== 0 && data.approved,
            },
            {
              id: "approve",
              label: "Analisar dados do agente",
              onClick: handleAllTableMenuClick,
              hide: (data) => !data.approved,
            },
          ]}
        />
      )}

      <ActivationModal
        title={`Deseja desativar o agente ${singlePoliceman?.user.name}`}
        content="Um agente desativado não poderá mais acessar
           à central. Contudo, basta ativá-lo novamente 
          para que o registro ocorra."
        modalIsOpen={deleteModal}
        setIsOpen={setDeleteModal}
        buttonText="Sim, desativar"
        onClick={() => {
          handlePolicemanDeleteSubmit({
            policeman: singlePoliceman,
          });
        }}
      />
      <ActivationModal
        title={`Deseja ativar o agente ${singlePoliceman?.user.name}`}
        content="Um agente ativado poderá requisitar 
          ocorrências à central. Contudo, basta desativá-lo para que isso não ocorra."
        modalIsOpen={activationModal}
        setIsOpen={setActivationModal}
        buttonText="Sim, ativar"
        onClick={() => {
          handlePolicemanActiveSubmit({
            policeman: singlePoliceman,
          });
        }}
      />
      <ActivationModal
        title={`Deseja declarar que o pronta resposta ${singlePoliceman?.user.name} foi pago`}
        content="Quando declarado a quantidade de dinheiro a receber é zerado e o novo valor contara a partir deste momento."
        modalIsOpen={paymentModal}
        setIsOpen={setPaymentModal}
        buttonText="Sim, declarar"
        onClick={() => {
          if (singlePoliceman)
            payPolicemanService(singlePoliceman.id).then((res) => {
              window.location.reload();
            });
        }}
      />

      {isModifiedMoreDetailsOpen &&
        modifiedPolicemanDetails.id !== undefined && (
          <MoreModifiedPolicemanDetailsModal
            modalIsOpen={isModifiedMoreDetailsOpen}
            setIsOpen={setIsModifiedMoreDetailsOpen}
            modifiedPoliceman={modifiedPolicemanDetails}
          />
        )}

      {moreInfoPoliceman && moreInfoPoliceman.id !== undefined && (
        <MorePolicemanDetailsModal
          modalIsOpen={moreDetails}
          setIsOpen={setMoreDetails}
          policeman={moreInfoPoliceman}
        />
      )}

      {registerModal && (
        <StepRegisterPoliceman
          opened={registerModal}
          setOpened={(value) => {
            setErrors({});
            setRegisterModal(value);
            setBankingData({
              accountData: {} as accountData,
            } as IBankingDataProps);
            setPoliceman({
              publicAgent: false,
              working: false,
              user: {} as IUser,
            } as IPolicemanRegister);
            setAddress({ city: "" } as IAddressProps);
            setVehicle({} as IVehicleProps);
            setMoreInfoPoliceman({} as IGetByIdPoliceman);
            resetPoliceman();
            setSinglePoliceman(undefined);
            if (hasChanges) {
              setHasChanges(false);
              window.location.reload();
            }
          }}
          policemanData={singlePoliceman}
          moreInfoPolicemen={moreInfoPoliceman}
        />
      )}

      <ExportEfficiencyReportModal
        isOpen={openEfficiencyModal}
        onRequestClose={() => {
          setOpenEfficiencyModal(false);
        }}
      />
    </DashboardTemplate>
  );
}
